import { useQuery } from "@tanstack/react-query";
import { createFileRoute, HeadContent } from "@tanstack/react-router";
import { useState } from "react";
import { RefreshCw, Search, AlertTriangle, CheckCircle, MapPin } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { GscSearchAnalyticsRow, GscSnapshotResult } from "@/lib/gsc.types";

export const Route = createFileRoute("/seo-dashboard")({
  component: SeoDashboardPage,
  head: () => ({
    meta: [
      { title: "Panel SEO | Google Search Console | SEOEVOLUTION" },
      {
        name: "description",
        content:
          "Panel interno de seguimiento SEO: indexación, errores, sitemap y consultas de Google Search Console para agenciaseoevolution.com.",
      },
      { property: "og:title", content: "Panel SEO | Google Search Console | SEOEVOLUTION" },
      {
        property: "og:description",
        content:
          "Panel interno de seguimiento SEO: indexación, errores, sitemap y consultas de Google Search Console.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://agenciaseoevolution.com/seo-dashboard" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://agenciaseoevolution.com/seo-dashboard" }],
  }),
});

function SeoDashboardPage() {
  const fetchSnapshot = async ({ data }: { data: { targetUrl: string } }) => {
    const res = await fetch(`/api/gsc?targetUrl=${encodeURIComponent(data.targetUrl)}`);
    const payload = await res.json();
    if (!res.ok) throw new Error(payload?.error ?? "Error al leer Search Console");
    return payload as GscSnapshotResult;
  };
  const refreshSnapshot = async ({ data }: { data: { targetUrl: string } }) => {
    const res = await fetch("/api/gsc", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    });
    const payload = await res.json();
    if (!res.ok) throw new Error(payload?.error ?? "Error al actualizar Search Console");
    return payload;
  };
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["gsc-snapshot"],
    queryFn: () => fetchSnapshot({ data: { targetUrl: "https://agenciaseoevolution.com" } }),
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshSnapshot({ data: { targetUrl: "https://agenciaseoevolution.com" } });
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const rows = data?.analytics?.rows ?? [];
  const totals = rows.reduce(
    (acc, row) => ({
      clicks: acc.clicks + (row.clicks ?? 0),
      impressions: acc.impressions + (row.impressions ?? 0),
      positionSum: acc.positionSum + (row.position ?? 0) * (row.impressions ?? 0),
      impressionsForPosition: acc.impressionsForPosition + (row.impressions ?? 0),
    }),
    { clicks: 0, impressions: 0, positionSum: 0, impressionsForPosition: 0 },
  );

  const avgPosition =
    totals.impressionsForPosition > 0
      ? (totals.positionSum / totals.impressionsForPosition).toFixed(1)
      : "—";
  const ctr = totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : "0.00";

  const sitemap = data?.sitemap;
  const errorCount = Number(sitemap?.errors ?? 0);
  const warningCount = Number(sitemap?.warnings ?? 0);
  const sitemapHealthy = errorCount === 0 && warningCount === 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <HeadContent />
      <main className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                Panel de Google Search Console
              </h1>
              <p className="text-muted-foreground mt-2">
                Seguimiento de indexación, errores y consultas orgánicas.
              </p>
            </div>
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              className="shrink-0"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              {isRefreshing ? "Actualizando…" : "Actualizar datos"}
            </Button>
          </div>

          {isLoading && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="h-20 bg-muted rounded-t-xl" />
                  <CardContent className="h-16 bg-muted/50" />
                </Card>
              ))}
            </div>
          )}

          {error && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">No se pudieron cargar los datos</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {(error as Error).message}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!isLoading && !error && data && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <Badge variant="outline" className="gap-1">
                  <MapPin className="h-3 w-3" />
                  {data.siteUrl}
                </Badge>
                <span>
                  Última actualización:{" "}
                  {data.refreshedAt
                    ? new Date(data.refreshedAt).toLocaleString("es-ES")
                    : "Sin datos"}
                </span>
                {data.stale && (
                  <Badge variant="secondary" className="text-amber-500 border-amber-500/20">
                    Datos de más de 24 h
                  </Badge>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <MetricCard
                  title="Clics totales"
                  value={totals.clicks.toLocaleString("es-ES")}
                  subtitle="Últimos 28 días"
                  icon={<Search className="h-5 w-5" />}
                />
                <MetricCard
                  title="Impresiones"
                  value={totals.impressions.toLocaleString("es-ES")}
                  subtitle="Últimos 28 días"
                  icon={<Search className="h-5 w-5" />}
                />
                <MetricCard
                  title="CTR medio"
                  value={`${ctr}%`}
                  subtitle="Clics / impresiones"
                  icon={<Search className="h-5 w-5" />}
                />
                <MetricCard
                  title="Posición media"
                  value={avgPosition}
                  subtitle="Ponderada por impresiones"
                  icon={<Search className="h-5 w-5" />}
                />
              </div>

              <div className="grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Top consultas orgánicas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {rows.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        No hay datos de consultas para el período seleccionado.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 font-medium">Consulta</th>
                              <th className="text-right py-2 font-medium">Clics</th>
                              <th className="text-right py-2 font-medium">Impresiones</th>
                              <th className="text-right py-2 font-medium">CTR</th>
                              <th className="text-right py-2 font-medium">Pos.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row: GscSearchAnalyticsRow, idx: number) => (
                              <tr key={idx} className="border-b border-border/50 last:border-0">
                                <td className="py-2 pr-4 max-w-xs truncate">
                                  {row.keys?.[0] ?? "—"}
                                </td>
                                <td className="py-2 text-right">{row.clicks?.toLocaleString("es-ES")}</td>
                                <td className="py-2 text-right">
                                  {row.impressions?.toLocaleString("es-ES")}
                                </td>
                                <td className="py-2 text-right">
                                  {row.ctr ? `${(row.ctr * 100).toFixed(2)}%` : "—"}
                                </td>
                                <td className="py-2 text-right">
                                  {row.position ? row.position.toFixed(1) : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Estado del sitemap</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!sitemap ? (
                      <p className="text-muted-foreground text-sm">Sin datos de sitemap.</p>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          {sitemapHealthy ? (
                            <CheckCircle className="h-6 w-6 text-emerald-500" />
                          ) : (
                            <AlertTriangle className="h-6 w-6 text-amber-500" />
                          )}
                          <div>
                            <p className="font-medium">
                              {sitemapHealthy ? "Sitemap correcto" : "Requiere atención"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {sitemap.path ?? "https://agenciaseoevolution.com/sitemap.xml"}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="rounded-lg border border-border p-3">
                            <p className="text-muted-foreground text-xs">Errores</p>
                            <p className="text-xl font-semibold">{errorCount}</p>
                          </div>
                          <div className="rounded-lg border border-border p-3">
                            <p className="text-muted-foreground text-xs">Advertencias</p>
                            <p className="text-xl font-semibold">{warningCount}</p>
                          </div>
                        </div>
                        {sitemap.lastSubmitted && (
                          <p className="text-xs text-muted-foreground">
                            Enviado:{" "}
                            {new Date(sitemap.lastSubmitted).toLocaleDateString("es-ES")}
                          </p>
                        )}
                        {sitemap.lastDownloaded && (
                          <p className="text-xs text-muted-foreground">
                            Descargado:{" "}
                            {new Date(sitemap.lastDownloaded).toLocaleDateString("es-ES")}
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </CardContent>
    </Card>
  );
}
